import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { colors } from "../../Shared/colors";
import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useRegisterUserMutation } from "../../features/authSlice";
import { Toaster, toast } from "react-hot-toast";
import { useGetAllRegionQuery } from "../../features/regionSlice";
import { decryptData } from "../../services/crypto";
import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  IconButton,
  Typography,
  Avatar,
  Box,
  InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

export const StaffFirst = ({
  filters = { search: "", role: "", region: "" },
  onFiltersChange = () => {},
  onClearFilters = () => {},
}) => {
  // Controlled filters. Each field writes straight into the page's state so the
  // table reacts as you type — no Search button to press, and nothing to forget
  // to press.
  //
  // Status is deliberately absent: it is the tab strip on the table below. Two
  // controls for one dimension is how you end up on the Blocked tab filtered to
  // "Active" and staring at an empty table wondering what you broke.
  const setFilter = (key) => (e) =>
    onFiltersChange({ ...filters, [key]: e.target.value });
  const hasFilters = Object.values(filters).some(Boolean);

  const [open, setOpen] = useState(false);

  let role = null;
  let storedStaffRole = localStorage.getItem("role");

  if (storedStaffRole) {
    try {
      role = decryptData(storedStaffRole);
    } catch (error) {
      console.error("Failed to decrypt data:", error);
    }
  }

  const [staff, setstaff] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phoneNum: "",
    cnic: "",
    address: "",
    gender: "",
    designation: "",
    profilePic: null,
    region: "",
    assignArea: "",
    role: "",
    empID: ''
  });
  const fileInputRef = useRef(null);

  let { data: region } = useGetAllRegionQuery();

  let [registerUser, { isLoading }] = useRegisterUserMutation();

  const handleStaffChange = (e) => {
    const { name, value } = e.target;
    setstaff((prevstate) => ({
      ...prevstate,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setstaff((prevstate) => ({
      ...prevstate,
      profilePic: e.target.files[0],
    }));
  };

  const handleRegisterStaff = (e) => {
    e.preventDefault();
    var formdata = new FormData();
    formdata.append("firstName", staff.firstName);
    formdata.append("lastName", staff.lastName);
    formdata.append("email", staff.email);
    formdata.append("password", staff.password);
    formdata.append("phoneNum", staff.phoneNum);
    formdata.append("cnic", staff.cnic);
    formdata.append("address", staff.address);
    formdata.append("gender", staff.gender);
    formdata.append("designation", staff.designation);
    formdata.append("profilePic", staff.profilePic);
    formdata.append("region", staff.region);
    formdata.append("assignArea", staff.assignArea);
    formdata.append("role", staff.role);
    formdata.append('empID', staff.empID)

    registerUser(formdata)
      .unwrap()
      .then(() => {
        toast.success("Staff added successfully");
        setstaff({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          phoneNum: "",
          cnic: "",
          address: "",
          gender: "",
          designation: "",
          profilePic: null,
          region: "",
          assignArea: "",
          role: "",
          empID: ""
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setOpen(false);
      })
      .catch((err) => {
        toast.error(err.data?.error?.message || "Something went wrong");
      });
  };

  /**
   * Which required fields are still empty.
   *
   * A thirteen-field dialog with a single Save button gives no feedback until
   * submit, and the server's rejection names one field at a time. This counts
   * what is outstanding as you type, so the gap is visible before pressing
   * anything.
   */
  const REQUIRED = [
    ["firstName", "First name"], ["lastName", "Last name"],
    ["email", "Email"], ["password", "Password"],
    ["phoneNum", "Phone number"], ["cnic", "CNIC"],
    ["address", "Address"], ["gender", "Gender"],
    ["designation", "Designation"],
  ];
  const missing = REQUIRED.filter(([k]) => !String(staff[k] ?? "").trim());
  const filled = REQUIRED.length - missing.length;
  const complete = missing.length === 0;

  const designations = [
    "dos", "doa", "account", "admin", "dm", "rsm", "sm", "manger",
    "abdm", "bdm", "bda", "am", "dcc", "telesale", "developer", "v3", "Hr", "aps"
  ];

  return (
    <Main>
      <HeaderSection>
        <Typography variant="h5" fontWeight="800" color={colors.brown}>
          Manage Staff
        </Typography>
        {role === "hr" || role === "account" || role === "admin" || role === 'ceo' ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            sx={{
              backgroundColor: colors.brown,
              '&:hover': { backgroundColor: '#1a6b6b' },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Add New Staff
          </Button>
        ) : null}
      </HeaderSection>

      <SearchContainer>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            {/* One field for the three things anyone actually types when
                looking for a person. Searching only by employee ID meant you
                had to already know the ID of the person you were looking up. */}
            <TextField
              fullWidth
              size="small"
              label="Search staff"
              type="search"
              InputLabelProps={{ shrink: true }}
              placeholder="Name, email or employee ID"
              value={filters.search}
              onChange={setFilter("search")}
              InputProps={{
                sx: { borderRadius: "8px", backgroundColor: colors.white },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: colors.textLight }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
                  select
              fullWidth
              size="small"
              label="Designation"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { disableScrollLock: true } }}
              value={filters.role}
              onChange={setFilter("role")}
              InputProps={{ sx: { borderRadius: "8px", backgroundColor: colors.white } }}
            >
              <MenuItem value=""><em>All designations</em></MenuItem>
              {designations.map((d) => (
                <MenuItem key={d} value={d}>{d.toUpperCase()}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
                  select
              fullWidth
              size="small"
              label="Region"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { disableScrollLock: true } }}
              value={filters.region}
              onChange={setFilter("region")}
              InputProps={{ sx: { borderRadius: "8px", backgroundColor: colors.white } }}
            >
              <MenuItem value=""><em>All regions</em></MenuItem>
              {region?.map((r) => (
                <MenuItem key={r._id} value={r._id}>{r.regionName}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              onClick={onClearFilters}
              disabled={!hasFilters}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: colors.textMed,
                borderRadius: "8px",
                minHeight: "40px",
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </SearchContainer>

      {/* Add Staff Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            // Full-bleed on phones — a 13-field form inside a floating card
            // leaves almost no room for the fields themselves.
            m: { xs: 0, sm: 4 },
            width: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
          borderBottom: `1px solid ${colors.inputBorderWhite}`,
          pb: 2
        }}>
          <Box>
            <Typography variant="h6" fontWeight="bold" color={colors.text}>
              Add New Staff Member
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
              <Box
                sx={{
                  width: 108, height: 4, borderRadius: 20,
                  backgroundColor: colors.inputWhite, overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${(filled / REQUIRED.length) * 100}%`,
                    height: "100%", borderRadius: 20,
                    backgroundColor: complete ? "#1B7F3B" : colors.brown,
                    transition: "width 220ms ease",
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: colors.textLight, fontWeight: 600 }}>
                {complete
                  ? "All required fields complete"
                  : `${filled} of ${REQUIRED.length} required fields`}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" onSubmit={handleRegisterStaff} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Personal Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color={colors.brown} fontWeight="700" mb={1}>
                  Personal Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  placeholder="Ahmed"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={staff.firstName}
                  onChange={handleStaffChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  placeholder="Raza"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={staff.lastName}
                  onChange={handleStaffChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  placeholder="ahmed.raza@jagiir.com"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={staff.email}
                  onChange={handleStaffChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  helperText="Minimum 8 characters"
                  placeholder="At least 8 characters"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={staff.password}
                  onChange={handleStaffChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  helperText="11 digits, no dashes"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  placeholder="03001234567"
                  required
                  name="phoneNum"
                  value={staff.phoneNum}
                  onChange={handleStaffChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  helperText="13 digits, no dashes"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="CNIC"
                  inputMode="numeric"
                  placeholder="3520112345671"
                  required
                  name="cnic"
                  value={staff.cnic}
                  onChange={handleStaffChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  placeholder="House 12, Gulberg III, Lahore"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Address"
                  name="address"
                  value={staff.address}
                  onChange={handleStaffChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                      InputLabelProps={{ shrink: true }}
                  select
                  fullWidth
                  label="Gender"
                  name="gender"
                  value={staff.gender}
                  onChange={handleStaffChange}
                  required
                  size="small"
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              {/* Job Info */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color={colors.brown} fontWeight="700" mb={1}>
                  Job Details
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Employee ID"
                  name="empID"
                  value={staff.empID}
                  onChange={handleStaffChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                      InputLabelProps={{ shrink: true }}
                  select
                  fullWidth
                  label="Designation"
                  name="designation"
                  value={staff.designation}
                  onChange={handleStaffChange}
                  required
                  size="small"
                >
                  {designations.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                      InputLabelProps={{ shrink: true }}
                  select
                  fullWidth
                  label="Role"
                  name="role"
                  value={staff.role}
                  onChange={handleStaffChange}
                  size="small"
                >
                  {designations.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                      InputLabelProps={{ shrink: true }}
                  select
                  fullWidth
                  label="Region"
                  name="region"
                  value={staff.region}
                  onChange={handleStaffChange}
                  size="small"
                >
                  {region?.map((r) => (
                    <MenuItem key={r._id} value={r._id}>{r.regionName}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Assign Area"
                  name="assignArea"
                  value={staff.assignArea}
                  onChange={handleStaffChange}
                  size="small"
                />
              </Grid>

              {/* File Upload */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color={colors.brown} fontWeight="700" mb={1}>
                  Profile Picture
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    sx={{ borderColor: colors.inputBorderWhite, color: colors.textMed }}
                  >
                    Upload Image
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      accept="image/*"
                    />
                  </Button>
                  {staff.profilePic && (
                    <Avatar
                      src={URL.createObjectURL(staff.profilePic)}
                      alt="Preview"
                      sx={{ width: 50, height: 50 }}
                    />
                  )}
                </Box>
              </Grid>

            </Grid>
          </Box>
        </DialogContent>
        {/* Sticky so Save stays reachable without scrolling a 13-field form,
            and the outstanding count is answered right where you act on it. */}
        <DialogActions
          sx={{
            p: { xs: 2, sm: 3 },
            borderTop: `1px solid ${colors.inputBorderWhite}`,
            position: "sticky",
            bottom: 0,
            backgroundColor: colors.white,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="caption"
            role="status"
            sx={{ color: complete ? "#1B7F3B" : colors.textLight, fontWeight: 600, mr: "auto" }}
          >
            {complete
              ? "Ready to save"
              : `Still needed: ${missing.map(([, label]) => label).join(", ")}`}
          </Typography>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '8px', color: colors.text, borderColor: colors.inputBorderWhite }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegisterStaff}
            variant="contained"
            disabled={isLoading || !complete}
            sx={{
              borderRadius: '8px',
              backgroundColor: colors.brown,
              '&:hover': { backgroundColor: '#1a6b6b' },
              minWidth: '120px'
            }}
          >
            {isLoading ? "Saving..." : "Save Staff"}
          </Button>
        </DialogActions>
      </Dialog>
      <Toaster />
    </Main>
  );
};

const Main = styled.div`
  /* Width and vertical rhythm now come from the page (StaffComponent), so this
     is only a card. It previously set its own max-width and margins, which is
     why it never lined up with the table below it. */
  width: 100%;
  background: ${colors.white};
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 16px 18px;
  border-bottom: 1px solid ${colors.inputBorderWhite};
`;

const SearchContainer = styled.div`
  padding: 14px 18px 16px 18px;
`;
